
/**
 * The additional languages a shopper can actually buy for a book.
 *
 * The base language is always included in the headline price, and the source
 * marks translations it doesn't sell with `available: false` — neither belongs
 * in the picker.
 */
export function buyableLanguages(product) {
  return (product?.pricing?.language_options ?? []).filter((o) => !o.is_base && o.available);
}

/** What the base language edition alone costs — the book's headline price. */
export function basePrice(product) {
  return product?.pricing?.is_free ? 0 : (product?.price ?? 0);
}

/**
 * What each chosen translation adds on top of the base edition, one entry per
 * language asked for: `[{ language, price }]`.
 *
 * Selections are validated against the product rather than trusted, so a stale
 * cart line naming a language that has since been withdrawn adds 0 — but it is
 * still listed, because a receipt that names the adaptation has to price it too.
 */
export function languageCharges(product, languages = []) {
  if (product?.pricing?.is_free) return languages.map((language) => ({ language, price: 0 }));

  const buyable = new Map(buyableLanguages(product).map((o) => [o.language, o.price]));
  return languages.map((language) => ({ language, price: buyable.get(language) ?? 0 }));
}

/**
 * What one copy costs: the base language plus each selected translation.
 *
 * The backend repeats this calculation authoritatively at checkout — see
 * book_mapper.unit_price, which is built from the same two pieces.
 */
export function unitPrice(product, languages = []) {
  return languageCharges(product, languages).reduce((sum, c) => sum + c.price, basePrice(product));
}

/**
 * What a priced line is made of, whoever priced it.
 *
 * A line reaches the receipt from two places: the cart, priced here from the
 * catalog, and an order, priced by the backend and echoed back in its own
 * casing. Both carry the same three facts — the base edition, what each
 * adaptation added, and the total — so they are read through one function
 * rather than each screen guessing at a field name. Guessing is what showed a
 * confirmed order's books at zero: the confirmation asked for `lineTotal`, a
 * field only the cart has.
 *
 * `itemised` is false for an order placed before lines carried their make-up:
 * there is a total to show, but nothing honest to break it into.
 */
export function lineBreakdown(item = {}) {
  const charges = item.languageCharges ?? item.language_charges ?? [];
  const base = item.basePrice ?? item.base_price ?? null;
  const qty = item.qty ?? 1;
  const unit = item.price ?? charges.reduce((sum, c) => sum + (c.price ?? 0), base ?? 0);
  const lineTotal = item.lineTotal ?? item.line_total ?? unit * qty;

  return { basePrice: base, charges, unit, qty, lineTotal, itemised: base !== null };
}

/**
 * Whether a product has a physical copy that needs posting.
 *
 * Books on playTUB are digital: they unlock inside the platform against the
 * buyer's login, so there is nothing to deliver and checkout must not ask for an
 * address. Printed copies aren't sold yet — this is the switch that turns the
 * delivery half of checkout back on when they are.
 */
export const requiresDelivery = (product) => product?.format === 'printed';

/** True when anything in the cart has to be shipped somewhere. */
export const cartRequiresDelivery = (items = []) => items.some(requiresDelivery);

/**
 * Cart identity: the book, and nothing else.
 *
 * The language adaptations chosen for a book are a property of that cart line,
 * not part of what identifies it. Keying on them too meant picking a different
 * set on the product page appended a *second* line for the same book — two
 * charges for one thing you cannot own twice, and playTUB records the
 * entitlement once per (user, book) regardless. Re-adding a book now modifies
 * the line already in the cart.
 */
export function lineKey(id) {
  return String(id);
}

/**
 * Order maths, ported from the prototype's totals().
 * Pure on purpose — when FastAPI starts returning authoritative totals, this
 * stays as the optimistic client-side preview.
 *
 * Every line must be priced in the same currency: these numbers are added
 * together, and there is no exchange rate to add across currencies with. Callers
 * group by currency first — see useCartItems.
 */
export function calcTotals({ items = [], discount = 0 }) {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
  // An absolute amount, quoted by the server for this exact cart — coupons come
  // as both percentages and flat sums, and only the coupon service knows which
  // this is. Clamped so a stale quote against a since-emptied cart cannot show a
  // negative total.
  const applied = Math.min(Math.max(discount, 0), subtotal);

  return {
    subtotal,
    discount: applied,
    total: subtotal - applied,
    requiresDelivery: cartRequiresDelivery(items),
  };
}
